import React, { useState, useEffect } from 'react';
import { RhLogo } from './RhLogo';
import { SearchAutocomplete } from './SearchAutocomplete';
import { subscribeToAuthChanges, AppUser } from '../lib/authService';
import { subscribeToSiteSettingsFromFirestore, getLocalSiteSettings, SiteSettings } from '../lib/siteSettingsService';
import {
  Sparkles,
  ShoppingBag,
  Heart,
  Search,
  User,
  ShieldCheck,
  Bot,
  Sun,
  Moon,
  Globe,
  SlidersHorizontal,
  ChevronDown,
  Layers,
  Mail,
  BookOpen,
  CreditCard,
  Car,
  Award,
  FileText,
  Landmark,
  UserCheck,
  Briefcase,
  FolderPlus,
  Menu,
  X
} from 'lucide-react';
import { Currency, Product, DocumentCategory } from '../types';
import { INITIAL_CATEGORIES, subscribeToCategoriesFromFirestore, CategoryModel } from '../lib/categoriesService';

interface NavbarProps {
  darkMode: boolean;
  setDarkMode: (val: boolean) => void;
  currency: Currency;
  setCurrency: (curr: Currency) => void;
  cartCount: number;
  wishlistCount: number;
  onOpenCart: () => void;
  onOpenWishlist: () => void;
  onOpenAI: () => void;
  activeTab: 'marketplace' | 'categories' | 'countries' | 'contact' | 'user' | 'admin' | 'search';
  setActiveTab: (tab: 'marketplace' | 'categories' | 'countries' | 'contact' | 'user' | 'admin' | 'search') => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  products?: Product[];
  onSelectProduct?: (product: Product) => void;
  onPerformSearch?: (query: string) => void;
  onSelectCategory?: (category: DocumentCategory) => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  darkMode,
  setDarkMode,
  currency,
  setCurrency,
  cartCount,
  wishlistCount,
  onOpenCart,
  onOpenWishlist,
  onOpenAI,
  activeTab,
  setActiveTab,
  searchQuery,
  setSearchQuery,
  products = [],
  onSelectProduct = (_p?: Product) => {},
  onPerformSearch = (_q?: string) => {},
  onSelectCategory,
}) => {
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [categoriesList, setCategoriesList] = useState<CategoryModel[]>(INITIAL_CATEGORIES);
  useEffect(() => {
    const unsub = subscribeToCategoriesFromFirestore((cats) => {
      setCategoriesList(cats.filter(c => c.status !== 'hidden').sort((a,b)=> (a.displayOrder||0) - (b.displayOrder||0)));
    });
    return () => unsub();
  }, []);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<AppUser | null>(null);
  const [siteSettings, setSiteSettings] = useState<SiteSettings>(getLocalSiteSettings());

  useEffect(() => {
    const unsubscribeAuth = subscribeToAuthChanges((u) => {
      setCurrentUser(u);
    });
    const unsubscribeSettings = subscribeToSiteSettingsFromFirestore((settings) => {
      setSiteSettings(settings);
    });
    return () => {
      unsubscribeAuth();
      unsubscribeSettings();
    };
  }, []);

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'BookOpen': return <BookOpen className="w-4 h-4 text-blue-400" />;
      case 'CreditCard': return <CreditCard className="w-4 h-4 text-purple-400" />;
      case 'Car': return <Car className="w-4 h-4 text-cyan-400" />;
      case 'Award': return <Award className="w-4 h-4 text-amber-400" />;
      case 'FileText': return <FileText className="w-4 h-4 text-sky-400" />;
      case 'Landmark': return <Landmark className="w-4 h-4 text-emerald-400" />;
      case 'Globe': return <Globe className="w-4 h-4 text-fuchsia-400" />;
      case 'UserCheck': return <UserCheck className="w-4 h-4 text-teal-400" />;
      case 'Briefcase': return <Briefcase className="w-4 h-4 text-indigo-400" />;
      case 'Layers': return <Layers className="w-4 h-4 text-pink-400" />;
      default: return <FolderPlus className="w-4 h-4 text-slate-400" />;
    }
  };

  const handleCategoryItemClick = (catName: string) => {
    setCategoryDropdownOpen(false);
    setMobileMenuOpen(false);
    if (onSelectCategory) {
      onSelectCategory(catName as DocumentCategory);
    } else {
      setActiveTab('categories');
    }
  };

  const handleNavTabClick = (tab: 'marketplace' | 'categories' | 'countries' | 'contact' | 'user' | 'admin' | 'search') => {
    setActiveTab(tab);
    setMobileMenuOpen(false);
  };

  return (
    <>
      <header className={`fixed w-full top-0 z-50 transition-colors duration-200 ${darkMode ? 'glass-nav text-white border-b border-white/5 shadow-[0_4px_30px_rgba(0,0,0,0.1)] bg-[#020617]/95' : 'glass-nav-light text-slate-900 border-b border-slate-200/50 shadow-sm bg-white/95'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-2 sm:gap-4">
        
        {/* Brand Logo */}
        <div 
          onClick={() => handleNavTabClick('marketplace')}
          className="flex items-center gap-2.5 sm:gap-3 cursor-pointer group select-none flex-shrink-0"
        >
          <div className="relative flex-shrink-0 w-9 h-9 sm:w-11 sm:h-11 aspect-square flex items-center justify-center group-hover:scale-105 transition-transform duration-300 overflow-hidden rounded-xl">
            {siteSettings.siteLogoHeaderUrl ? (
              <img
                src={siteSettings.siteLogoHeaderUrl}
                alt={siteSettings.siteTitle || 'RH Editography'}
                className="w-full h-full object-contain"
              />
            ) : (
              <RhLogo className="w-full h-full" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-extrabold text-base sm:text-xl tracking-tight text-gradient">
                {siteSettings.siteTitle || 'RH Editography'}
              </span>
            </div>
            <p className="text-[9px] sm:text-[11px] font-medium opacity-60 tracking-wider uppercase hidden sm:block">
              {siteSettings.siteTaglineHeader || 'Digital Template Marketplace'}
            </p>
          </div>
        </div>

        {/* Center Navigation Links & Quick Search (Desktop lg+) */}
        <div className="hidden lg:flex items-center gap-3">
          <nav className="flex items-center gap-1 p-1.5 rounded-2xl bg-slate-900 border border-white/10">
            <button
              onClick={() => handleNavTabClick('marketplace')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all duration-300 ${
                activeTab === 'marketplace'
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/20'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800/50 hover:shadow-[0_0_15px_rgba(59,130,246,0.6)] hover:ring-1 hover:ring-blue-400/50'
              }`}
            >
              Marketplace
            </button>
            
            {/* Categories Menu Button with Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setCategoryDropdownOpen(true)}
              onMouseLeave={() => setCategoryDropdownOpen(false)}
            >
              <button
                onClick={() => handleNavTabClick('categories')}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all duration-300 flex items-center gap-1.5 ${
                  activeTab === 'categories'
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md shadow-purple-500/20'
                    : 'text-slate-300 hover:text-white hover:bg-slate-800/50 hover:shadow-[0_0_15px_rgba(168,85,247,0.6)] hover:ring-1 hover:ring-purple-400/50'
                }`}
              >
                <Layers className="w-3.5 h-3.5 text-purple-400" />
                <span>Categories</span>
                <ChevronDown className="w-3 h-3 text-slate-400" />
              </button>

              {/* Dropdown Box */}
              {categoryDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-64 p-2 rounded-2xl bg-slate-950/95 border border-slate-800 shadow-2xl z-50 grid grid-cols-1 gap-1">
                  <div className="px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-widest text-cyan-400 border-b border-slate-800/80 mb-1 flex items-center justify-between">
                    <span>Dynamic Categories</span>
                  </div>
                  {categoriesList.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryItemClick(cat.name)}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-bold text-slate-300 hover:text-white hover:bg-slate-900 transition-colors text-left"
                    >
                      {getIcon(cat.iconName)}
                      <span>{cat.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => handleNavTabClick('countries')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all duration-300 flex items-center gap-1.5 ${
                activeTab === 'countries'
                  ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-md shadow-cyan-500/20'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800/50 hover:shadow-[0_0_15px_rgba(6,182,212,0.6)] hover:ring-1 hover:ring-cyan-400/50'
              }`}
            >
              <Globe className="w-3.5 h-3.5" />
              Countries
            </button>

            <button
              onClick={() => handleNavTabClick('contact')}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all duration-300 flex items-center gap-1.5 ${
                activeTab === 'contact'
                  ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-md shadow-emerald-500/20'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800/50 hover:shadow-[0_0_15px_rgba(16,185,129,0.6)] hover:ring-1 hover:ring-emerald-400/50'
              }`}
            >
              <Mail className="w-3.5 h-3.5" />
              Contact
            </button>
          </nav>

          {/* Quick Search Autocomplete */}
          <div className="relative w-52 xl:w-72">
            <SearchAutocomplete
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              onSelectProduct={onSelectProduct}
              onPerformSearch={onPerformSearch}
              products={products}
              darkMode={darkMode}
              size="sm"
              placeholder="Search Passport, ID Card, PSD..."
            />
          </div>
        </div>

        {/* Right Actions Header (Theme Mode, Shopping Cart, Sign In, Hamburger Toggle) */}
        <div className="flex items-center gap-1.5 sm:gap-3">
          
          {/* 1. Theme Mode Toggle Button */}
          <button
            onClick={() => setDarkMode(!darkMode)}
            className={`p-2 sm:p-2.5 rounded-xl border transition-all duration-200 flex items-center justify-center ${
              darkMode 
                ? 'bg-slate-800/80 border-slate-700 hover:bg-slate-700 text-amber-300 shadow-sm' 
                : 'bg-slate-100 border-slate-300 hover:bg-slate-200 text-indigo-600 shadow-sm'
            }`}
            title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          {/* 2. Shopping Cart Icon Button */}
          <button
            onClick={onOpenCart}
            className="relative p-2 sm:p-2.5 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500 text-white font-bold text-xs shadow-lg shadow-blue-500/25 hover:shadow-cyan-500/40 hover:scale-105 active:scale-95 transition-all duration-300 flex items-center justify-center"
            title="Shopping Cart"
          >
            <ShoppingBag className="w-4 h-4" />
            {cartCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-white text-blue-700 font-extrabold text-[10px] min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center shadow-md">
                {cartCount}
              </span>
            )}
          </button>

          {/* 3. Account / Sign In Dynamic Header Button */}
          <button
            onClick={() => handleNavTabClick('user')}
            className={`flex items-center gap-1.5 px-2.5 sm:px-3.5 py-2 sm:py-2.5 rounded-xl border text-xs font-bold transition-all duration-300 shadow-sm ${
              activeTab === 'user'
                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-purple-400 shadow-md shadow-purple-500/25'
                : darkMode
                ? 'bg-slate-800/80 border-slate-700 hover:bg-slate-800/50 text-slate-200 hover:shadow-[0_0_15px_rgba(139,92,246,0.6)] hover:ring-1 hover:ring-violet-400/50 hover:border-transparent'
                : 'bg-slate-100 border-slate-300 hover:bg-white text-slate-800 hover:shadow-[0_0_15px_rgba(139,92,246,0.4)] hover:ring-1 hover:ring-violet-400/50 hover:border-transparent'
            }`}
            title="Account Dashboard"
          >
            <User className="w-4 h-4 text-purple-400" />
            <span className="hidden sm:inline">{currentUser ? 'Account' : 'Sign In'}</span>
          </button>

          {/* 4. Mobile Menu Hamburger Toggle (visible on < lg screens) */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden p-2 sm:p-2.5 rounded-xl border transition-all duration-200 flex items-center justify-center ${
              darkMode 
                ? 'bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700' 
                : 'bg-slate-100 border-slate-300 text-slate-800 hover:bg-slate-200'
            }`}
            aria-label="Toggle navigation menu"
          >
            {mobileMenuOpen ? <X className="w-5 h-5 text-cyan-400" /> : <Menu className="w-5 h-5 text-cyan-400" />}
          </button>

        </div>
      </div>

      {/* Mobile Navigation Drawer Overlay (< lg) */}
      {mobileMenuOpen && (
        <div className={`lg:hidden border-t px-4 pt-4 pb-6 space-y-4 shadow-2xl transition-all ${
          darkMode ? 'bg-slate-950/98 border-slate-800 text-white' : 'bg-white/98 border-slate-200 text-slate-900'
        }`}>
          {/* Quick Search on Mobile */}
          <div className="w-full">
            <SearchAutocomplete
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              onSelectProduct={(p) => {
                setMobileMenuOpen(false);
                onSelectProduct(p);
              }}
              onPerformSearch={(q) => {
                setMobileMenuOpen(false);
                onPerformSearch(q);
              }}
              products={products}
              darkMode={darkMode}
              size="sm"
              placeholder="Search templates..."
            />
          </div>

          {/* Navigation Links */}
          <nav className="grid grid-cols-1 gap-1.5 font-bold text-sm">
            <button
              onClick={() => handleNavTabClick('marketplace')}
              className={`px-4 py-2.5 rounded-xl text-left transition-all flex items-center justify-between ${
                activeTab === 'marketplace'
                  ? 'bg-blue-600 text-white'
                  : darkMode
                  ? 'hover:bg-slate-900 text-slate-200'
                  : 'hover:bg-slate-100 text-slate-800'
              }`}
            >
              <span>Marketplace</span>
              <Sparkles className="w-4 h-4 text-cyan-400" />
            </button>

            {/* Expandable Mobile Categories */}
            <div>
              <button
                onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
                className={`w-full px-4 py-2.5 rounded-xl text-left transition-all flex items-center justify-between ${
                  activeTab === 'categories'
                    ? 'bg-purple-600 text-white'
                    : darkMode
                    ? 'hover:bg-slate-900 text-slate-200'
                    : 'hover:bg-slate-100 text-slate-800'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-purple-400" />
                  <span>Categories</span>
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileCategoriesOpen ? 'rotate-180' : ''}`} />
              </button>

              {mobileCategoriesOpen && (
                <div className="pl-4 pr-2 py-2 mt-1 space-y-1 bg-slate-900 rounded-xl border border-white/5 max-h-56 overflow-y-auto">
                  <button
                    onClick={() => handleNavTabClick('categories')}
                    className="w-full text-left px-3 py-1.5 text-xs font-extrabold text-cyan-400 hover:underline"
                  >
                    View All Categories →
                  </button>
                  {categoriesList.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryItemClick(cat.name)}
                      className="w-full text-left px-3 py-2 rounded-lg text-xs font-semibold text-slate-300 hover:bg-slate-800 hover:text-white flex items-center gap-2"
                    >
                      {getIcon(cat.iconName)}
                      <span>{cat.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => handleNavTabClick('countries')}
              className={`px-4 py-2.5 rounded-xl text-left transition-all flex items-center justify-between ${
                activeTab === 'countries'
                  ? 'bg-cyan-600 text-white'
                  : darkMode
                  ? 'hover:bg-slate-900 text-slate-200'
                  : 'hover:bg-slate-100 text-slate-800'
              }`}
            >
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-cyan-400" />
                <span>Countries</span>
              </div>
            </button>

            <button
              onClick={() => handleNavTabClick('contact')}
              className={`px-4 py-2.5 rounded-xl text-left transition-all flex items-center justify-between ${
                activeTab === 'contact'
                  ? 'bg-emerald-600 text-white'
                  : darkMode
                  ? 'hover:bg-slate-900 text-slate-200'
                  : 'hover:bg-slate-100 text-slate-800'
              }`}
            >
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-emerald-400" />
                <span>Contact Support</span>
              </div>
            </button>
          </nav>
        </div>
      )}
    </header>
    {/* Spacer to prevent content from hiding behind fixed navbar */}
    <div className="h-16 sm:h-20 w-full shrink-0"></div>
    </>
  );
};

